import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { getUserQueryKey } from "src/config/request-keys";
import { invalidateQualityEvents } from "src/hooks/queries/quality-event-mutations";
import { captureSentryException } from "src/utils/sentry";
import { ToolCallName } from "./messages/utils/tool-calls";
import { getChatMessageType, type ChatMessage } from "./types";

const getToolCallNamesById = (messages: ChatMessage[]) => {
  const names = new Map<string, string>();

  messages.forEach((message) => {
    if (getChatMessageType(message) !== "ai") return;

    message.tool_calls?.forEach((toolCall) => {
      if (
        typeof toolCall.id === "string" &&
        typeof toolCall.name === "string"
      ) {
        names.set(toolCall.id, toolCall.name);
      }
    });
  });

  return names;
};

export const useInvalidateQualityEventsOnToolApply = ({
  messages,
  orgId,
}: {
  messages: ChatMessage[] | undefined;
  orgId: string;
}) => {
  const queryClient = useQueryClient();
  const processedToolCallIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!messages?.length) return;

    const toolCallNamesById = getToolCallNamesById(messages);
    const newlyProcessedToolCallIds = new Set<string>();
    let userChanged = false;

    messages.forEach((message) => {
      if (
        getChatMessageType(message) !== "tool" ||
        message.status !== "success"
      ) {
        return;
      }

      const toolCallId = message.tool_call_id;
      if (
        typeof toolCallId !== "string" ||
        processedToolCallIdsRef.current.has(toolCallId)
      ) {
        return;
      }

      const toolCallName =
        typeof message.name === "string" && message.name.length > 0
          ? message.name
          : toolCallNamesById.get(toolCallId);

      if (
        toolCallName !== ToolCallName.EDIT_QUALITY_EVENT_RECORD &&
        toolCallName !== ToolCallName.ADD_USER_TO_FUNCTIONAL_GROUP
      ) {
        return;
      }

      newlyProcessedToolCallIds.add(toolCallId);
      if (toolCallName === ToolCallName.ADD_USER_TO_FUNCTIONAL_GROUP)
        userChanged = true;
    });

    if (newlyProcessedToolCallIds.size === 0) return;

    const toolCallIds = Array.from(newlyProcessedToolCallIds);
    newlyProcessedToolCallIds.forEach((toolCallId) => {
      processedToolCallIdsRef.current.add(toolCallId);
    });

    void Promise.all([
      invalidateQualityEvents(queryClient, orgId),
      ...(userChanged
        ? [queryClient.invalidateQueries({ queryKey: getUserQueryKey() })]
        : []),
    ]).catch((error) => {
      captureSentryException(error, { orgId, toolCallIds });
    });
  }, [messages, orgId, queryClient]);
};
